import { useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PowerBIEmbed } from "powerbi-client-react";
import { models, Report } from "powerbi-client";
import {
  getEmbedToken,
} from "@/services/report-service";
import logoIcon from "../../assets/logo-icon.svg";
import { EmbedTokenRequest } from "@/types/report";
import { useUser } from "@/hooks/useUser";

declare global {
  interface Window {
    report: Report;
  }
}

export function ReportScreen() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const {userInfo} = useUser();
  const report = location.state?.report;

  const embedTokenRequest: EmbedTokenRequest = {
    clientId: 57844,
    userId: userInfo?.userId ?? null,
    reportId: Number(id),
  };

  const {
    data: embedData,
    isLoading,
  } = useQuery({
    queryKey: ["embedToken", embedTokenRequest],
    queryFn: () => getEmbedToken(embedTokenRequest),
    enabled: !!id,
  });

  return (
    <>
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{report.name}</CardTitle>
          <CardDescription>{report.description}</CardDescription>
        </CardHeader>
      </Card>
      <Card>
        <CardContent className="p-0">
        {isLoading ? (
            <div className="w-[1280px] h-[700px] flex items-center justify-center bg-gray-100">
              <div className="animate-pulse">
                <img
                  src={logoIcon}
                  alt="Logo"
                  className="w-32 h-32 animate-grow-shrink"
                />
              </div>
            </div>
          ) : embedData ? (
            <PowerBIEmbed
              embedConfig={{
                type: "report",
                id: report.reportId,
                embedUrl: report.embedUrl,
                accessToken: embedData.embedToken,
                tokenType: models.TokenType.Embed,
                settings: {
                  layoutType: models.LayoutType.Custom,
                  customLayout: {
                    displayOption: models.DisplayOption.FitToPage,
                  },
                  panes: {
                    filters: {
                      expanded: false,
                      visible: false,
                    },
                  },
                  background: models.BackgroundType.Transparent,
                },
              }}
              eventHandlers={
                new Map([
                  ["loaded", function () { console.log("Report loaded"); }],
                  ["rendered", function () { console.log("Report rendered"); }],
                  ["error", function (event) { console.log(event?.detail); }],
                  ["visualClicked", () => console.log("visual clicked")],
                  ["pageChanged", (event) => console.log(event)],
                ])
              }
              cssClassName="w-[1280px] h-[700px]"
              getEmbeddedComponent={(embeddedReport) => {
                window.report = embeddedReport as Report;
              }}
            />
          ) : (
            <div>Failed to load embed data</div>
          )}
        </CardContent>
      </Card>
    </>
  );
}